import { Fragment, useEffect, useState } from 'react'
import { api, type ClassificationFinding, type FindingKind, type Reconciliation as Findings } from '../api'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'
import { RowMenu, type MenuItem } from '../components/RowMenu'
import { asAt } from '../verdictWords'

/**
 * Reconcile with holdings — TECHNICAL_DESIGN.md §5.3.2, IMPLEMENTATION.md decision 50. The Snapshot Evaluator pack.
 *
 * The classification codes each fund's newest holdings carry, set against the Dictionary
 * and the codes limits name. **A limit naming a code no holding carries comes first**:
 * matching is exact, so it measures nothing and reads as within its limit. Each finding
 * is decided by a person: a held code is requested as a member, through the Dictionary's
 * own approval, or a finding that needs nothing done is dismissed with a note and can be
 * reopened. Nothing in the Dictionary or in a limit changes on its own.
 */

const KINDS: FindingKind[] = ['cited_unheld', 'undefined', 'defined_unheld']

const KIND_LABEL: Record<FindingKind, string> = {
  cited_unheld: 'Named by a limit, not held',
  undefined: 'Held, not in the Dictionary',
  defined_unheld: 'Entered by hand, not held',
}

/** The same colours as verdicts: a limit measuring nothing is as loud as a breach. */
const KIND_BADGE: Record<FindingKind, string> = {
  cited_unheld: 'breach',
  undefined: 'warn',
  defined_unheld: 'not_run',
}

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const keyOf = (f: ClassificationFinding) => `${f.kind}:${f.scheme}:${f.code}`

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

function finding(f: ClassificationFinding): string {
  switch (f.kind) {
    case 'cited_unheld': {
      const limits = f.limits ?? []
      return `Named by ${limits.join(', ')}. No fund’s newest holdings carry it, so ${
        limits.length === 1 ? 'that limit measures' : 'those limits measure'
      } nothing.`
    }
    case 'undefined':
      return `${plural(f.holdings ?? 0, 'holding')} in ${plural(f.funds?.length ?? 0, 'fund')} carry it (${(f.funds ?? []).join(', ')}). A limit naming this classification does not match them.`
    case 'defined_unheld':
      return `Entered as “${f.name}”. No fund’s newest holdings carry it.`
  }
}

export function Reconciliation({ canDismiss, canRequest }: { canDismiss: boolean; canRequest: boolean }) {
  const [data, setData] = useState<Findings | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [acting, setActing] = useState<{ key: string; mode: 'dismiss' | 'request' } | null>(null)
  const [note, setNote] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      setData(await api.reconciliation())
    } catch (e) {
      setError(said(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function start(f: ClassificationFinding, mode: 'dismiss' | 'request') {
    setActing({ key: keyOf(f), mode })
    setNote('')
    setName(f.code)
    setError(null)
    setDone(null)
  }

  async function act(work: () => Promise<Findings | null>, message: string) {
    setBusy(true)
    setError(null)
    try {
      const next = await work()
      if (next) setData(next)
      else await load()
      setActing(null)
      setDone(message)
    } catch (e) {
      setError(said(e))
    } finally {
      setBusy(false)
    }
  }

  function actions(f: ClassificationFinding): MenuItem[] {
    const items: MenuItem[] = []
    if (f.kind === 'undefined' && canRequest) {
      items.push({
        label: 'Request as a member',
        disabled: f.requested,
        why: f.requested ? 'Already requested, waiting for an approver.' : undefined,
        onSelect: () => start(f, 'request'),
      })
    }
    if (canDismiss) items.push({ label: 'Dismiss', onSelect: () => start(f, 'dismiss') })
    return items
  }

  const days = [...new Set((data?.snapshots ?? []).map((s) => asAt(s.as_of)))].sort()

  return (
    <section className="reconciliation">
      <header className="page-head">
        <h2>Reconcile with holdings</h2>
        <p className="note">
          The classification codes each fund’s newest holdings carry, set against the Dictionary and the codes limits
          name. Each disagreement is listed for a person to decide; nothing in the Dictionary or in a limit changes on
          its own. Part of the <EvaluatorName strong={false} />.
          <AddOnFootnote />
        </p>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p className="done" role="status">
          {done}
        </p>
      )}

      {data === null ? (
        !error && <p className="loading">Loading…</p>
      ) : data.snapshots.length === 0 ? (
        <p className="empty">No holdings loaded yet. Load a snapshot, and its classification codes are reconciled here.</p>
      ) : (
        <>
          <p className="note" data-testid="reconcile-scope">
            Across {plural(data.snapshots.length, 'fund')}’ newest holdings, as at {days.join(', ')}.
          </p>
          <div className="whatif-counts" data-testid="reconcile-counts">
            {KINDS.map((k) => (
              <span
                key={k}
                className={`verdict-count ${KIND_BADGE[k]}${data.counts[k] === 0 ? ' zero' : ''}`}
                data-testid={`reconcile-count-${k}`}
              >
                {data.counts[k]} {KIND_LABEL[k]}
              </span>
            ))}
          </div>

          {data.open.length === 0 ? (
            <p className="empty" data-testid="reconcile-clear">
              The holdings, the Dictionary and the limits agree on every classification code.
            </p>
          ) : (
            <div className="table-scroll">
              <table className="grid stacked" data-testid="reconcile-open">
                <thead>
                  <tr>
                    <th>Classification</th>
                    <th>Code</th>
                    <th>Finding</th>
                    <th>Nearest</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.open.map((f) => {
                    const key = keyOf(f)
                    const items = actions(f)
                    return (
                      <Fragment key={key}>
                        <tr data-testid="reconcile-finding">
                          <td>{f.scheme_name}</td>
                          <td>
                            <code>{f.code}</code>
                          </td>
                          <td>
                            <span className={`verdict-count ${KIND_BADGE[f.kind]}`}>{KIND_LABEL[f.kind]}</span>
                            <span className="clause">{finding(f)}</span>
                            {f.requested && <span className="clause">Requested as a member, waiting for an approver.</span>}
                          </td>
                          <td>{f.nearest.length ? f.nearest.join(', ') : '—'}</td>
                          <td className="run-actions">
                            {items.length > 0 && <RowMenu label={`Actions for ${f.scheme_name} ${f.code}`} items={items} />}
                          </td>
                        </tr>
                        {acting?.key === key && (
                          <tr className="whatif-new">
                            <td colSpan={5}>
                              {acting.mode === 'dismiss' ? (
                                <div className="cure-form">
                                  <label>
                                    Why this needs nothing done
                                    <textarea
                                      rows={2}
                                      value={note}
                                      placeholder="Such as: the custodian’s cash line, not an issuer group"
                                      onChange={(e) => setNote(e.target.value)}
                                    />
                                  </label>
                                  <p className="actions">
                                    <button
                                      type="button"
                                      disabled={busy || !note.trim()}
                                      onClick={() =>
                                        void act(() => api.dismissFinding(f, note), `${f.scheme_name} ${f.code} dismissed.`)
                                      }
                                    >
                                      Dismiss
                                    </button>
                                    <button type="button" className="ghost" onClick={() => setActing(null)}>
                                      Cancel
                                    </button>
                                  </p>
                                </div>
                              ) : (
                                <div className="cure-form">
                                  <label>
                                    The member’s name, as the Dictionary shows it
                                    <input value={name} onChange={(e) => setName(e.target.value)} />
                                  </label>
                                  <p className="actions">
                                    <button
                                      type="button"
                                      disabled={busy || !name.trim()}
                                      onClick={() =>
                                        void act(async () => {
                                          await api.requestMember(f.scheme, f.code, name)
                                          return null
                                        }, `${f.code} requested as a member of ${f.scheme_name}. An approver decides under Term requests.`)
                                      }
                                    >
                                      Request
                                    </button>
                                    <button type="button" className="ghost" onClick={() => setActing(null)}>
                                      Cancel
                                    </button>
                                  </p>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.not_carried.length > 0 && (
            <p className="note" data-testid="reconcile-not-carried">
              No snapshot carries a column for {data.not_carried.map((s) => s.name).join(', ')} yet, so the codes limits
              name or entered by hand there are reconciled once a holdings file maps one.
            </p>
          )}

          {data.dismissed.length > 0 && (
            <>
              <h3>Dismissed</h3>
              <div className="table-scroll">
                <table className="grid stacked" data-testid="reconcile-dismissed">
                  <thead>
                    <tr>
                      <th>Classification</th>
                      <th>Code</th>
                      <th>Finding</th>
                      <th>Why</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.dismissed.map((f) => (
                      <tr key={keyOf(f)} data-testid="reconcile-dismissed-finding">
                        <td>{f.scheme_name}</td>
                        <td>
                          <code>{f.code}</code>
                        </td>
                        <td>{KIND_LABEL[f.kind]}</td>
                        <td>
                          {f.decision?.note}
                          <span className="clause">
                            {f.decision?.decided_by ?? ''}
                            {f.decision && ` · ${new Date(f.decision.decided_at).toLocaleString()}`}
                          </span>
                        </td>
                        <td className="run-actions">
                          {canDismiss && (
                            <RowMenu
                              label={`Actions for dismissed ${f.scheme_name} ${f.code}`}
                              items={[
                                {
                                  label: 'Reopen',
                                  onSelect: () =>
                                    void act(() => api.reopenFinding(f), `${f.scheme_name} ${f.code} reopened.`),
                                },
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}
